import {
  User,
  Workout,
  WorkoutRoutine,
  Exercise,
  CreateWorkoutRequest,
  CreateWorkoutRoutineRequest,
  PatchWorkoutRoutineRequest,
  CreateExerciseRequest,
  PatchWorkoutRequest,
  HealthCheckResponse,
  ErrorResponse,
  FriendRequest,
  CreateFriendRequest,
  SharedWorkoutRoutine,
  PatchSharedWorkoutRoutineRequest,
  PaginatedWorkoutsResponse,
  PaginatedActivityFeedResponse,
} from "./types";
import { auth } from "../firebaseConfig.ts";

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    return {};
  }

  const token = await user.getIdToken();
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = "An error occurred";
    try {
      const error: ErrorResponse = await response.json();
      errorMessage = error.error || errorMessage;
    } catch (e) {
      // If parsing fails, use status text
      errorMessage = response.statusText || errorMessage;
    }
    throw new ApiError(response.status, errorMessage);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  // Check if response has content
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }

  // For non-JSON responses, try to parse as JSON anyway
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    console.error("Failed to parse response:", text);
    throw new ApiError(500, "Invalid response format");
  }
}

// Health check
export async function checkHealth(): Promise<HealthCheckResponse> {
  const response = await fetch(`${API_BASE_URL}/health`);
  return handleResponse<HealthCheckResponse>(response);
}

// User API
export async function getUserProfile(): Promise<User> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/profile`, {
    headers: authHeaders,
  });
  return handleResponse<User>(response);
}

export async function patchUserProfile(
  profileData: Partial<User>,
): Promise<User> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/profile`, {
    method: "PATCH",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(profileData),
  });
  return handleResponse<User>(response);
}

export async function getFriends(): Promise<Partial<User>[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/friends`, {
    headers: authHeaders,
  });
  return handleResponse<Partial<User>[]>(response);
}

export async function getFriendRequests(): Promise<FriendRequest[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/friend-requests`, {
    headers: authHeaders,
  });
  return handleResponse<FriendRequest[]>(response);
}

export async function getOutgoingFriendRequests(): Promise<FriendRequest[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/friend-requests/outgoing`, {
    headers: authHeaders,
  });
  return handleResponse<FriendRequest[]>(response);
}

export async function acceptFriendRequest(
  id: string,
): Promise<FriendRequest[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/friend-requests/${id}/accept`, {
    method: "POST",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
  });
  return handleResponse<FriendRequest[]>(response);
}

export async function declineFriendRequest(
  id: string,
): Promise<FriendRequest[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/friend-requests/${id}/decline`,
    {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
    },
  );
  return handleResponse<FriendRequest[]>(response);
}

export async function createFriendRequest(
  request: CreateFriendRequest,
): Promise<FriendRequest> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/friend-requests`, {
    method: "POST",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });
  return handleResponse<FriendRequest>(response);
}

export async function deleteFriendRequest(id: string): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/friend-requests/${id}`, {
    method: "DELETE",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
  });
  return handleResponse<void>(response);
}

export async function deleteFriend(id: string): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/friends/${id}`, {
    method: "DELETE",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
  });
  return handleResponse<void>(response);
}

export async function shareWorkoutRoutine(
  friendId: string,
  routineId: string,
): Promise<FriendRequest[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/workout-routines/${routineId}/share`,
    {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ recipient_id: friendId }),
    },
  );
  return handleResponse<FriendRequest[]>(response);
}

// Activity feed API

export async function getActivityFeed(
  from?: Date,
  to?: Date,
  page?: number,
  pageSize?: number,
): Promise<PaginatedActivityFeedResponse> {
  const params = new URLSearchParams();
  if (from) {
    params.append("from", from.toISOString());
  }
  if (to) {
    params.append("to", to.toISOString());
  }
  if (page !== undefined) {
    params.append("page", page.toString());
  }
  if (pageSize !== undefined) {
    params.append("page_size", pageSize.toString());
  }

  const authHeaders = await getAuthHeaders();
  const url = `${API_BASE_URL}/activity${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await fetch(url, {
    headers: authHeaders,
  });
  return handleResponse<PaginatedActivityFeedResponse>(response);
}

export async function likeActivityFeedItem(
  activityFeedItemId: string,
): Promise<string> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/activity/${activityFeedItemId}/like`,
    {
      method: "POST",
      headers: {
        ...authHeaders,
      },
    },
  );
  return handleResponse<string>(response);
}

export async function unlikeActivityFeedItem(
  activityFeedItemId: string,
): Promise<string> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/activity/${activityFeedItemId}/unlike`,
    {
      method: "POST",
      headers: {
        ...authHeaders,
      },
    },
  );
  return handleResponse<string>(response);
}

// Workouts API
export async function getWorkouts(
  from?: Date,
  to?: Date,
  page?: number,
  pageSize?: number,
  status?: string,
): Promise<PaginatedWorkoutsResponse> {
  const params = new URLSearchParams();
  if (from) {
    params.append("from", from.toISOString());
  }
  if (to) {
    params.append("to", to.toISOString());
  }
  if (page !== undefined) {
    params.append("page", page.toString());
  }
  if (pageSize !== undefined) {
    params.append("page_size", pageSize.toString());
  }
  if (status !== undefined) {
    params.append("status", status);
  }

  const authHeaders = await getAuthHeaders();
  const url = `${API_BASE_URL}/workouts${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await fetch(url, {
    headers: authHeaders,
  });
  return handleResponse<PaginatedWorkoutsResponse>(response);
}

export async function getWorkoutById(id: string): Promise<Workout> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workouts/${id}`, {
    headers: authHeaders,
  });
  return handleResponse<Workout>(response);
}

export async function createWorkout(
  workout: CreateWorkoutRequest,
): Promise<Workout> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workouts`, {
    method: "POST",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(workout),
  });
  return handleResponse<Workout>(response);
}

export async function deleteWorkout(id: string): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workouts/${id}`, {
    method: "DELETE",
    headers: authHeaders,
  });
  return handleResponse<void>(response);
}

export async function patchWorkout(
  id: string,
  workout: Partial<PatchWorkoutRequest>,
): Promise<Workout> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workouts/${id}`, {
    method: "PATCH",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(workout),
  });
  return handleResponse<Workout>(response);
}

export async function completeWorkout(workoutId: string): Promise<Workout> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/workouts/${workoutId}/complete`,
    {
      method: "POST",
      headers: {
        ...authHeaders,
      },
    },
  );
  return handleResponse<Workout>(response);
}

// Workout Routines API
export async function getWorkoutRoutines(): Promise<WorkoutRoutine[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workout-routines`, {
    headers: authHeaders,
  });
  return handleResponse<WorkoutRoutine[]>(response);
}

export async function getSharedWorkoutRoutines(): Promise<
  SharedWorkoutRoutine[]
> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/shared-workout-routines`, {
    headers: authHeaders,
  });
  return handleResponse<SharedWorkoutRoutine[]>(response);
}

export async function importWorkoutRoutine(
  routineId: string,
): Promise<WorkoutRoutine> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/shared-workout-routines/${routineId}/import`,
    {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
    },
  );
  return handleResponse<WorkoutRoutine>(response);
}

export async function patchSharedWorkoutRoutine(
  id: string,
  routine: Partial<PatchSharedWorkoutRoutineRequest>,
): Promise<SharedWorkoutRoutine> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/shared-workout-routines/${id}`,
    {
      method: "PATCH",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(routine),
    },
  );
  return handleResponse<SharedWorkoutRoutine>(response);
}

export async function deleteSharedWorkoutRoutine(id: string): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/shared-workout-routines/${id}`,
    {
      method: "DELETE",
      headers: authHeaders,
    },
  );
  return handleResponse<void>(response);
}

export async function getWorkoutRoutineById(
  id: string,
): Promise<WorkoutRoutine> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workout-routines/${id}`, {
    headers: authHeaders,
  });
  return handleResponse<WorkoutRoutine>(response);
}

export async function createWorkoutRoutine(
  routine: CreateWorkoutRoutineRequest,
): Promise<WorkoutRoutine> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workout-routines`, {
    method: "POST",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(routine),
  });
  return handleResponse<WorkoutRoutine>(response);
}

export async function deleteWorkoutRoutine(id: string): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workout-routines/${id}`, {
    method: "DELETE",
    headers: authHeaders,
  });
  return handleResponse<void>(response);
}

export async function patchWorkoutRoutine(
  id: string,
  routine: Partial<PatchWorkoutRoutineRequest>,
): Promise<WorkoutRoutine> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workout-routines/${id}`, {
    method: "PATCH",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(routine),
  });
  return handleResponse<WorkoutRoutine>(response);
}

// Exercises API
export async function getExercises(): Promise<Exercise[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/exercises`, {
    headers: authHeaders,
  });
  return handleResponse<Exercise[]>(response);
}

export async function getExerciseById(id: string): Promise<Exercise> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/exercises/${id}`, {
    headers: authHeaders,
  });
  return handleResponse<Exercise>(response);
}

export async function createExercise(
  exercise: CreateExerciseRequest,
): Promise<Exercise> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/exercises`, {
    method: "POST",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(exercise),
  });
  return handleResponse<Exercise>(response);
}

export async function patchExercise(
  id: string,
  exercise: Partial<CreateExerciseRequest>,
): Promise<Exercise> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/exercises/${id}`, {
    method: "PATCH",
    headers: {
      ...authHeaders,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(exercise),
  });
  return handleResponse<Exercise>(response);
}

export async function deleteExercise(id: string): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/exercises/${id}`, {
    method: "DELETE",
    headers: authHeaders,
  });
  return handleResponse<void>(response);
}

export async function mergeExercise(
  targetExerciseId: string,
  sourceExerciseId: string,
): Promise<{ message: string }> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${API_BASE_URL}/exercises/${targetExerciseId}/merge`,
    {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id: sourceExerciseId }),
    },
  );
  return handleResponse<{ message: string }>(response);
}

// Export the ApiError for error handling
export { ApiError };
